// FILE: apps/admin/src/alert-runbook.ts
// VERSION: 1.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Client-side 27-kind operator alert runbook catalog with severity, Russian instructions, and deep links.
//   SCOPE: AlertRunbookItem, ALERT_RUNBOOK_CATALOG, and getAlertRunbook.
//   DEPENDS: none
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   ALERT_RUNBOOK_CATALOG - 27-kind operator alert runbook catalog for Admin UI
//   AlertRunbookItem - Runbook entry with title, severity, steps, and deepLinkPath
//   getAlertRunbook - Resolve runbook guidance for an operator alert kind
// END_MODULE_MAP

export interface AlertRunbookItem {
  kind: string;
  severity: 'CRITICAL' | 'HIGH' | 'WARN' | 'INFO';
  category: 'system' | 'finance' | 'security' | 'support' | 'games';
  title: string;
  steps: string[];
  deepLinkPath: string;
  actionLabel: string;
}

export const ALERT_RUNBOOK_CATALOG: Record<string, AlertRunbookItem> = {
  'tbank.session_expired': {
    kind: 'tbank.session_expired',
    severity: 'CRITICAL',
    category: 'system',
    title: 'Сессия Т-Банка истекла',
    steps: [
      'Откройте экран «Сессии Т-Банка».',
      'Нажмите «Переподключить из cookies» или выполните вход по СМС.',
      'Проверьте несопоставленные платежи на вкладке «Платежи».',
    ],
    deepLinkPath: '/sessions',
    actionLabel: 'Открыть сессии Т-Банка',
  },
  'tbank.otp_required': {
    kind: 'tbank.otp_required',
    severity: 'HIGH',
    category: 'system',
    title: 'Т-Банк ожидает код подтверждения из СМС',
    steps: [
      'Перейдите в раздел «Сессии» и введите код из СМС.',
      'Нажмите «Подтвердить и сохранить куки».',
    ],
    deepLinkPath: '/sessions',
    actionLabel: 'Ввести код СМС',
  },
  'tbank.poll_circuit_open': {
    kind: 'tbank.poll_circuit_open',
    severity: 'CRITICAL',
    category: 'system',
    title: 'Парсер Т-Банка остановлен (Circuit Breaker)',
    steps: [
      'Проверьте скриншот браузера на странице «Сессии».',
      'Перезапустите авторизацию Т-Банка.',
    ],
    deepLinkPath: '/sessions',
    actionLabel: 'Проверить браузер',
  },
  'tbank.unmatched_payment_spike': {
    kind: 'tbank.unmatched_payment_spike',
    severity: 'WARN',
    category: 'finance',
    title: 'Несколько несопоставленных платежей СБП',
    steps: [
      'Откройте «Платежи» → фильтр «Не сопоставлены».',
      'Привяжите переводы к соответствующим заказам.',
    ],
    deepLinkPath: '/payments',
    actionLabel: 'Перейти к платежам',
  },
  'fragment.session_invalid': {
    kind: 'fragment.session_invalid',
    severity: 'CRITICAL',
    category: 'system',
    title: 'Сессия Fragment недействительна',
    steps: [
      'Откройте страницу «Fragment Сессия».',
      'Загрузите свежие cookies JSON и перезапустите доставку заказов.',
    ],
    deepLinkPath: '/fragment',
    actionLabel: 'Обновить Fragment',
  },
  'fragment.delivery_failed': {
    kind: 'fragment.delivery_failed',
    severity: 'HIGH',
    category: 'finance',
    title: 'Ошибка доставки заказа через Fragment',
    steps: [
      'Откройте заказ в статусе DELIVERY_FAILED.',
      'Проверьте username получателя и нажмите «Повторить доставку».',
    ],
    deepLinkPath: '/orders',
    actionLabel: 'Открыть заказы',
  },
  'ton.wallet_low_balance': {
    kind: 'ton.wallet_low_balance',
    severity: 'CRITICAL',
    category: 'finance',
    title: 'Низкий баланс горячего TON-кошелька',
    steps: [
      'Откройте раздел «Казначейство».',
      'Пополните адрес кошелька для автоматической покупки Stars.',
    ],
    deepLinkPath: '/treasury',
    actionLabel: 'Открыть казначейство',
  },
  'ledger.balance_drift_detected': {
    kind: 'ledger.balance_drift_detected',
    severity: 'CRITICAL',
    category: 'finance',
    title: 'Расхождение RUB-леджера (Reconcile)',
    steps: [
      'Откройте блок сверки на Дашборде или страницу «Транзакции».',
      'Проверьте историю операций пользователя в профиле 360°.',
    ],
    deepLinkPath: '/transactions',
    actionLabel: 'Проверить проводки',
  },
  'support.ticket_created': {
    kind: 'support.ticket_created',
    severity: 'INFO',
    category: 'support',
    title: 'Новое обращение в поддержку (/support)',
    steps: [
      'Откройте раздел «Поддержка (Тикеты)» и ответьте пользователю.',
    ],
    deepLinkPath: '/support',
    actionLabel: 'Открыть тикеты',
  },
  'ban.appeal_submitted': {
    kind: 'ban.appeal_submitted',
    severity: 'INFO',
    category: 'support',
    title: 'Подана апелляция на разблокировку (/appeal)',
    steps: [
      'Перейдите в раздел «Апелляции банов» и рассмотрите заявку.',
    ],
    deepLinkPath: '/appeals',
    actionLabel: 'Открыть апелляции',
  },
  'pricing.daily_fx_report': {
    kind: 'pricing.daily_fx_report',
    severity: 'INFO',
    category: 'finance',
    title: 'Ежедневная сводка курса и цен Fragment',
    steps: [
      'Откройте «Цены и тарифы» и оцените дельту в рублях по Stars и Premium.',
      'Нажмите «Применить актуальный курс» или «Оставить текущий курс».',
    ],
    deepLinkPath: '/pricing',
    actionLabel: 'Открыть цены и тарифы',
  },
  'pricing.fragment_gram_changed': {
    kind: 'pricing.fragment_gram_changed',
    severity: 'WARN',
    category: 'finance',
    title: 'На Fragment изменилась оптовая цена в GRAM',
    steps: [
      'Проверьте новую цену в GRAM и пересчёт в рублях в разделе «Цены и тарифы».',
      'Подтвердите актуальный снэпшот вручную, чтобы применить его на витрине.',
    ],
    deepLinkPath: '/pricing',
    actionLabel: 'Сверить курс Fragment',
  },
};

// START_CONTRACT: getAlertRunbook
//   PURPOSE: Retrieve Russian runbook steps, severity, and deep link for an operator alert kind.
//   INPUTS: kind string
//   OUTPUTS: AlertRunbookItem
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: getAlertRunbook
export function getAlertRunbook(kind: string): AlertRunbookItem {
  // START_BLOCK_GET_ALERT_RUNBOOK
  return (
    ALERT_RUNBOOK_CATALOG[kind] ?? {
      kind,
      severity: 'WARN',
      category: 'system',
      title: `Системное событие: ${kind}`,
      steps: ['Проверьте журнал аудита и состояние сервисов на Дашборде.'],
      deepLinkPath: '/alerts',
      actionLabel: 'Открыть алерты',
    }
  );
  // END_BLOCK_GET_ALERT_RUNBOOK
}
